// Floating bulk-actions bar that appears at the bottom when products are selected.
import { useState } from "react";
import { toast } from "sonner";
import {
  Tag as TagIcon, DollarSign, Boxes, Download, X, Loader2,
  Eye, EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  bulkPrice, bulkStock, bulkStatus, bulkTags, exportCsvUrl,
} from "@/lib/products";

export default function BulkBar({ selectedIds, onClear, onDone }) {
  const [busy, setBusy] = useState(false);

  const run = async (fn, label) => {
    setBusy(true);
    const tid = toast.loading(`Aplic ${label}...`);
    try {
      const r = await fn();
      if (r.ok) {
        toast.success(`${label} — ${r.affected}/${r.total} produse actualizate${r.failed ? ` (${r.failed} eșuate)` : ""}`, { id: tid });
        onDone?.();
      } else {
        toast.error(`${label} eșuat`, { id: tid });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Eroare ${label}`, { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 bg-card border border-border rounded-2xl shadow-2xl px-3 py-2 flex items-center gap-2"
      data-testid="products-bulk-bar"
    >
      <div className="px-2 text-sm font-medium" data-testid="products-bulk-count">
        {selectedIds.length} selectate
      </div>
      <div className="h-5 w-px bg-border" />

      {/* PRICE */}
      <BulkPricePopover
        disabled={busy}
        onApply={(payload) => run(() => bulkPrice({ ...payload, product_ids: selectedIds }), "preț bulk")}
      />

      {/* STOCK */}
      <BulkStockPopover
        disabled={busy}
        onApply={(payload) => run(() => bulkStock({ ...payload, product_ids: selectedIds }), "stoc bulk")}
      />

      {/* TAGS */}
      <BulkTagsPopover
        disabled={busy}
        onApply={(payload) => run(() => bulkTags({ ...payload, product_ids: selectedIds }), "tag-uri bulk")}
      />

      {/* STATUS */}
      <Button
        size="sm"
        variant="outline"
        className="h-8"
        onClick={() => run(() => bulkStatus({ product_ids: selectedIds, status: "ACTIVE" }), "Activează")}
        disabled={busy}
        data-testid="bulk-activate"
      >
        <Eye className="h-3.5 w-3.5 mr-1.5" /> Activează
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-8"
        onClick={() => run(() => bulkStatus({ product_ids: selectedIds, status: "DRAFT" }), "Draft")}
        disabled={busy}
        data-testid="bulk-draft"
      >
        <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Draft
      </Button>

      {/* EXPORT */}
      <Button
        size="sm"
        variant="outline"
        className="h-8"
        asChild
        data-testid="bulk-export"
      >
        <a href={exportCsvUrl(selectedIds)} download>
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export
        </a>
      </Button>

      <div className="h-5 w-px bg-border" />
      <button
        onClick={onClear}
        className="text-muted-foreground hover:text-foreground p-1"
        title="Anulează selecția"
        data-testid="bulk-clear"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
      </button>
    </div>
  );
}


function BulkPricePopover({ disabled, onApply }) {
  const [field, setField] = useState("price");
  const [mode, setMode] = useState("set");
  const [value, setValue] = useState("");

  const submit = () => {
    const v = parseFloat(value);
    if (Number.isNaN(v)) return toast.error("Valoare invalidă");
    onApply({ field, mode, value: v });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" disabled={disabled} data-testid="bulk-price-btn">
          <DollarSign className="h-3.5 w-3.5 mr-1.5" /> Preț
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest">Modifică preț</div>
        <Select value={field} onValueChange={setField}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="price">Preț redus</SelectItem>
            <SelectItem value="compare_at_price">Preț întreg</SelectItem>
          </SelectContent>
        </Select>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="h-9" data-testid="bulk-price-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="set">Setează la</SelectItem>
            <SelectItem value="increase_pct">Crește cu %</SelectItem>
            <SelectItem value="decrease_pct">Scade cu %</SelectItem>
            <SelectItem value="increase_amt">Crește cu RON</SelectItem>
            <SelectItem value="decrease_amt">Scade cu RON</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={mode === "set" ? "ex: 99.99" : (mode.includes("pct") ? "ex: 10" : "ex: 5")}
          data-testid="bulk-price-value"
        />
        <Button size="sm" className="w-full" onClick={submit} data-testid="bulk-price-apply">
          Aplică
        </Button>
      </PopoverContent>
    </Popover>
  );
}


function BulkStockPopover({ disabled, onApply }) {
  const [mode, setMode] = useState("set");
  const [value, setValue] = useState("");

  const submit = () => {
    const v = parseInt(value, 10);
    if (Number.isNaN(v)) return toast.error("Valoare invalidă");
    onApply({ mode, value: v });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" disabled={disabled} data-testid="bulk-stock-btn">
          <Boxes className="h-3.5 w-3.5 mr-1.5" /> Stoc
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest">Modifică stoc</div>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="h-9" data-testid="bulk-stock-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="set">Setează la</SelectItem>
            <SelectItem value="increase">Adaugă</SelectItem>
            <SelectItem value="decrease">Scade</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="ex: 10"
          data-testid="bulk-stock-value"
        />
        <Button size="sm" className="w-full" onClick={submit} data-testid="bulk-stock-apply">
          Aplică
        </Button>
      </PopoverContent>
    </Popover>
  );
}


function BulkTagsPopover({ disabled, onApply }) {
  const [add, setAdd] = useState("");
  const [remove, setRemove] = useState("");

  const submit = () => {
    const a = add.split(",").map((s) => s.trim()).filter(Boolean);
    const r = remove.split(",").map((s) => s.trim()).filter(Boolean);
    if (!a.length && !r.length) return toast.error("Adaugă cel puțin un tag");
    onApply({ add: a, remove: r });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" disabled={disabled} data-testid="bulk-tags-btn">
          <TagIcon className="h-3.5 w-3.5 mr-1.5" /> Tag-uri
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest">Modifică tag-uri</div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Adaugă (separate prin virgulă)</div>
          <Input
            value={add}
            onChange={(e) => setAdd(e.target.value)}
            placeholder="ex: vip, promo"
            data-testid="bulk-tags-add"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Elimină</div>
          <Input
            value={remove}
            onChange={(e) => setRemove(e.target.value)}
            placeholder="ex: old, deprecated"
            data-testid="bulk-tags-remove"
          />
        </div>
        <Button size="sm" className="w-full" onClick={submit} data-testid="bulk-tags-apply">
          Aplică
        </Button>
      </PopoverContent>
    </Popover>
  );
}
